package fr.frux.core.ads

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import fr.frux.core.ads.resources.Res
import fr.frux.core.ads.resources.ads_label
import org.jetbrains.compose.resources.stringResource

/**
 * Où en est la bannière de carte (ADR 0030, #529) : [claimed] quand l'arbitrage lui donne
 * l'emplacement, [loaded] quand la régie a livré une annonce. Elle n'occupe de place qu'une fois
 * les deux vrais ([showing]) : tant que l'annonce n'est pas arrivée, sa vue reste rattachée à zéro
 * de hauteur pour pouvoir charger et rien n'apparaît.
 */
data class MapBannerState(
    val claimed: Boolean,
    val loaded: Boolean,
) {
    val showing: Boolean get() = claimed && loaded
}

/**
 * Réclame l'emplacement de la bannière de carte tant que [requested] est vrai et dit où elle en
 * est. Seule la claim gagnante de l'arbitrage s'affiche : ni pendant l'interstitiel ni sous la
 * liste native, et jamais hors éligibilité (première session, Frux+, hold, consentement, plateforme
 * sans pub).
 *
 * Séparé de [MapBannerAd] pour que l'écran réserve de la place seulement quand la bannière est
 * réellement affichable.
 */
@Composable
fun rememberMapBannerState(requested: Boolean): MapBannerState {
    val controller = LocalAdsController.current
    val state by controller.state.collectAsState()
    var claimId by remember(controller) { mutableStateOf<Long?>(null) }
    DisposableEffect(controller, requested) {
        if (requested) {
            val claim = controller.claim(AdPlacement.MAP_BANNER)
            claimId = claim.id
            onDispose { claim.release() }
        } else {
            claimId = null
            onDispose {}
        }
    }
    val claimed = requested && claimId != null && state.visibleClaimId == claimId
    return MapBannerState(claimed = claimed, loaded = rememberPlatformBannerLoaded())
}

/**
 * Bannière de carte. À composer quand [MapBannerState.claimed] est vrai : seule cette vue charge l'annonce. La vue
 * publicitaire n'est pas créée ici : elle vit côté plateforme et survit aux navigations (ADR 0009),
 * le composable ne fait que l'héberger. Cibles sans pub : rien n'est composé.
 *
 * [onRemoveAds] : lien « Regarder une vidéo pour 24 h sans pub » sous l'annonce (#531), affiché seulement quand la
 * vidéo récompensée est proposable. `null` pour la vue de chargement à hauteur nulle.
 */
@Composable
fun MapBannerAd(
    modifier: Modifier = Modifier,
    onRemoveAds: (() -> Unit)? = null,
    removeAdsLoading: Boolean = false,
) {
    Column(modifier = modifier.fillMaxWidth()) {
        PlatformBannerAd(modifier = Modifier.fillMaxWidth())
        if (onRemoveAds != null) {
            RewardedOfferLink(
                onClick = onRemoveAds,
                loading = removeAdsLoading,
                modifier = Modifier.align(Alignment.End),
            )
        }
    }
}

/** Mention « Annonce » au-dessus de l'annonce, partagée par les cibles qui en affichent une. */
@Composable
internal fun BannerLabel(modifier: Modifier = Modifier) {
    Text(
        text = stringResource(Res.string.ads_label),
        style = MaterialTheme.typography.labelSmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier = modifier,
    )
}

/** Vrai quand la vue de la régie a reçu une annonce. Cibles sans pub : jamais. */
@Composable
internal expect fun rememberPlatformBannerLoaded(): Boolean

/** Hébergement de la vue de la régie. Android : la bannière adaptative de [GmaAdsPlatform]. */
@Composable
internal expect fun PlatformBannerAd(modifier: Modifier)
